import { useMemo, useState } from 'react';

import { apiClient } from '@infrastructure/api/client';
import { AdminListRepository } from '@infrastructure/api/AdminListRepository';
import { DataTable, type DataTableColumn } from '@app/components/data-table/DataTable';

interface ContactRow {
  id: string;
  customer_email: string;
  customer_company: string;
  phone: string;
  first_name: string;
  last_name: string;
  email: string;
  tags: string[];
  opt_in: boolean;
  opted_out: boolean | null;
  hidden_at: string | null;
  created_at: string;
}

const repo = new AdminListRepository<ContactRow>(apiClient, 'contacts');

const columns: DataTableColumn<ContactRow>[] = [
  { id: 'phone', header: 'Phone', accessor: (r) => <code className="font-mono text-xs">{r.phone}</code> },
  { id: 'name', header: 'Name', accessor: (r) => `${r.first_name} ${r.last_name}`.trim() || '—' },
  { id: 'email', header: 'Email', accessor: (r) => r.email || '—' },
  {
    id: 'customer_email', header: 'Customer',
    accessor: (r) => (
      <span>
        {r.customer_company}
        <span className="block text-xs text-slate-500">{r.customer_email}</span>
      </span>
    ),
  },
  { id: 'tags', header: 'Tags', accessor: (r) => (r.tags ?? []).join(', ') || '—' },
  {
    id: 'consent',
    header: 'Consent',
    accessor: (r) => (
      <span className={r.opt_in && !r.opted_out ? 'text-green-700' : 'text-red-700'}>
        {r.opted_out ? 'opted-out' : r.opt_in ? 'opted-in' : 'unknown'}
        {r.hidden_at && (
          <span className="block text-xs text-slate-500" title="Deleted by the customer; kept so the opt-out still blocks sends">
            hidden from customer
          </span>
        )}
      </span>
    ),
  },
  { id: 'created_at', header: 'Added', accessor: (r) => new Date(r.created_at).toLocaleDateString() },
];

export function ContactsPage() {
  const [search, setSearch] = useState('');
  const filters = useMemo<Record<string, string>>(() => {
    const f: Record<string, string> = {};
    if (search) f.search = search;
    return f;
  }, [search]);

  return (
    <section>
      <div className="mb-3 flex flex-wrap items-end justify-between gap-2">
        <h2 className="text-lg font-semibold">Contacts</h2>
        <input
          type="search"
          placeholder="Search phone, name, email, customer"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="rounded border px-2 py-1 text-sm"
        />
      </div>
      <DataTable<ContactRow>
        columns={columns}
        fetcher={(p) => repo.list({ ...p, filters })}
        pagination="offset"
        defaultPageSize={50}
        rowKey={(r) => r.id}
        emptyState={<span data-testid="datatable-empty">No contacts yet.</span>}
      />
    </section>
  );
}
